import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { useAuth } from '@/context/AuthProvider';

import { COMMUNITY_POSTS_QUERY_KEY, GROUP_POSTS_QUERY_KEY } from './useGetPosts';

export type ApiRequest_Create_Post = {
  title: string;
  content: string;
  is_pinned?: boolean;
  metadata?: Record<string, unknown>;
};

export type ApiRequest_Create_Community_Post = {
  communityId: number;
} & ApiRequest_Create_Post;

export type ApiRequest_Create_Group_Post = {
  groupId: number;
} & ApiRequest_Create_Post;

export type ApiResponse_Create_Post = {
  id: number;
  title: string;
  content: string;
  is_pinned: boolean;
  created_at: string;
  updated_at: string;
  community_id?: number;
  group_negotiation_id?: number;
  author: {
    id: number;
    user: {
      id: string;
      full_name: string;
      email: string;
    };
  };
  _count: {
    replies: number;
    likes: number;
  };
};

const COMMUNITY_METHOD = 'POST';
const COMMUNITY_ENDPOINT = 'posts/community/{communityId}';

const GROUP_METHOD = 'POST';
const GROUP_ENDPOINT = 'posts/group/{groupId}';

export const useCreateCommunityPost = () => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: ApiRequest_Create_Community_Post) => {
      try {
        const { communityId, ...postData } = params;
        const data = await handleApiCall<ApiResponse_Create_Post, ApiRequest_Create_Post>(
          session,
          COMMUNITY_METHOD,
          getResolvedEndpoint(COMMUNITY_ENDPOINT, {
            communityId: communityId.toString(),
          }),
          postData
        );

        enqueueSnackbar('Post created successfully!', { variant: 'success' });
        return data;
      } catch (err) {
        enqueueSnackbar('Failed to create post.', { variant: 'error' });
        throw err;
      }
    },
    onSuccess: (_, variables) => {
      // Invalidate community posts using the correct query key
      queryClient.invalidateQueries({
        queryKey: COMMUNITY_POSTS_QUERY_KEY({ 
          communityId: variables.communityId, 
          page: 1, 
          limit: 20 
        }),
      });
      // Also invalidate other pages
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey as unknown[];
          const firstKey = queryKey[0];
          if (Array.isArray(firstKey) && typeof firstKey[1] === 'string') {
            return firstKey[1].includes(`posts/community/${variables.communityId}`);
          }
          return false;
        },
      });
    },
  });
};

export const useCreateGroupPost = () => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: ApiRequest_Create_Group_Post) => {
      try {
        const { groupId, ...postData } = params;
        const data = await handleApiCall<ApiResponse_Create_Post, ApiRequest_Create_Post>(
          session,
          GROUP_METHOD,
          getResolvedEndpoint(GROUP_ENDPOINT, {
            groupId: groupId.toString(),
          }),
          postData
        );

        enqueueSnackbar('Post created successfully!', { variant: 'success' });
        return data;
      } catch (err) {
        enqueueSnackbar('Failed to create post.', { variant: 'error' });
        throw err;
      }
    },
    onSuccess: (_, variables) => {
      // Invalidate group posts using the correct query key
      queryClient.invalidateQueries({
        queryKey: GROUP_POSTS_QUERY_KEY({ 
          groupId: variables.groupId, 
          page: 1, 
          limit: 20 
        }),
      });
      // Also invalidate other pages
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey as unknown[];
          const firstKey = queryKey[0];
          if (Array.isArray(firstKey) && typeof firstKey[1] === 'string') {
            return firstKey[1].includes(`posts/group/${variables.groupId}`);
          }
          return false;
        },
      });
    },
  });
};